import "./simple-pdf-tool.css";
import "./pdf-to-jpg.css";
import { createZipBlob, openBrowserPdf, pdfErrorMessage, renderPageBlob } from "./pdf-browser-core.js";
import { createOperationResult } from "./operation-result.js";
import { JPG_PRESETS, jpgPageName, jpgZipName } from "./pdf-to-jpg-core.js";

const root = document.querySelector("[data-pdf-to-jpg]");

if (root) {
  const ids = ["jpgStatus", "jpgUploadPanel", "jpgDropZone", "chooseJpgPdf", "jpgFileInput", "jpgWorkspace", "jpgFileName", "jpgPageCount", "replaceJpgPdf", "jpgPreview", "jpgQuality", "convertPdfToJpg", "jpgProgress"];
  const elements = Object.fromEntries(ids.map(id => [id, document.getElementById(id)]));
  const state = { document: null, fileName: "document.pdf", pageCount: 0, busy: false, previewUrl: null };
  const operationResult = createOperationResult(root, elements.jpgWorkspace);

  function showStatus(message = "") { elements.jpgStatus.textContent = message; elements.jpgStatus.hidden = !message; }
  function updateControls() { elements.replaceJpgPdf.disabled = state.busy; elements.jpgQuality.disabled = state.busy; elements.convertPdfToJpg.disabled = state.busy || !state.document; }
  async function updatePreview() {
    const page = await state.document.getPage(1);
    try {
      const blob = await renderPageBlob(page, { dpi: 110, maxWidth: 560, maxHeight: 560, type: "image/jpeg", quality: .82 });
      if (state.previewUrl) URL.revokeObjectURL(state.previewUrl);
      state.previewUrl = URL.createObjectURL(blob); elements.jpgPreview.src = state.previewUrl;
    } finally { page.cleanup(); }
  }

  async function loadFile(file) {
    if (state.busy) return;
    showStatus(); state.busy = true; updateControls(); let loaded = null;
    try {
      loaded = await openBrowserPdf(file, { maxPages: 50 });
      if (state.document) await state.document.destroy();
      state.document = loaded.document; state.fileName = loaded.fileName; state.pageCount = loaded.pageCount; loaded = null;
      elements.jpgFileName.textContent = state.fileName; elements.jpgPageCount.textContent = String(state.pageCount);
      elements.jpgUploadPanel.hidden = true; elements.jpgWorkspace.hidden = false;
      await updatePreview();
    } catch (error) { if (loaded?.document) await loaded.document.destroy(); showStatus(pdfErrorMessage(error)); }
    finally { state.busy = false; elements.jpgFileInput.value = ""; updateControls(); }
  }

  async function convert() {
    if (state.busy || !state.document) return;
    showStatus(); state.busy = true; elements.jpgProgress.hidden = false; updateControls();
    try {
      const preset = JPG_PRESETS[elements.jpgQuality.value] || JPG_PRESETS.standard;
      const entries = [];
      for (let index = 1; index <= state.pageCount; index += 1) {
        elements.jpgProgress.textContent = `Converting page ${index} of ${state.pageCount}`;
        const page = await state.document.getPage(index);
        try {
          const blob = await renderPageBlob(page, { dpi: preset.dpi, type: "image/jpeg", quality: preset.quality });
          entries.push({ name: jpgPageName(state.fileName, index, state.pageCount), bytes: new Uint8Array(await blob.arrayBuffer()) });
        } finally { page.cleanup(); }
      }
      operationResult.show(entries.length === 1
        ? { blob: new Blob([entries[0].bytes], { type: "image/jpeg" }), fileName: entries[0].name, message: "Your PDF page was converted successfully.", details: ["1 JPG image"] }
        : { blob: createZipBlob(entries), fileName: jpgZipName(state.fileName), message: "Your PDF pages were converted successfully.", details: [`${entries.length} JPG images in the ZIP`] });
    } catch (error) { showStatus(error?.message || "smallpdf.app could not convert this PDF."); }
    finally { state.busy = false; elements.jpgProgress.hidden = true; updateControls(); }
  }

  elements.chooseJpgPdf.addEventListener("click", event => { event.stopPropagation(); elements.jpgFileInput.click(); });
  elements.replaceJpgPdf.addEventListener("click", () => elements.jpgFileInput.click());
  elements.jpgFileInput.addEventListener("change", () => loadFile(elements.jpgFileInput.files?.[0]));
  elements.convertPdfToJpg.addEventListener("click", convert);
  elements.jpgDropZone.addEventListener("click", event => { if (!event.target.closest("button")) elements.jpgFileInput.click(); });
  elements.jpgDropZone.addEventListener("keydown", event => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); elements.jpgFileInput.click(); } });
  elements.jpgDropZone.addEventListener("dragover", event => { event.preventDefault(); elements.jpgDropZone.classList.add("is-dragging"); });
  elements.jpgDropZone.addEventListener("dragleave", () => elements.jpgDropZone.classList.remove("is-dragging"));
  elements.jpgDropZone.addEventListener("drop", event => { event.preventDefault(); elements.jpgDropZone.classList.remove("is-dragging"); loadFile(event.dataTransfer.files?.[0]); });
  window.addEventListener("beforeunload", () => { operationResult.dispose(); if (state.previewUrl) URL.revokeObjectURL(state.previewUrl); if (state.document) state.document.destroy(); });
}
